import { DEFAULT_BRAND } from './brandDefaults';
import { normalizeHex } from './colorMath';

type ColorKey = 'primary_color' | 'accent_color' | 'success_color' | 'base_color';

const DEFAULT_BY_KEY: Record<ColorKey, string> = {
  primary_color: DEFAULT_BRAND.primaryColor,
  accent_color: DEFAULT_BRAND.accentColor,
  success_color: DEFAULT_BRAND.successColor,
  base_color: DEFAULT_BRAND.baseColor,
};

export interface BrandColorPayload {
  primary_color?: string;
  accent_color?: string;
  success_color?: string;
  base_color?: string;
  unset?: ColorKey[];
}

/**
 * Turn a set of chosen brand colours into a config update. A colour the tenant actually customised is
 * written as an override. A colour equal to the platform default is NOT written: it is cleared (`unset`),
 * so the tenant keeps inheriting the platform default. Writing the default back would pin a stale copy that
 * outlives later changes to it, which is how tenants ended up stuck on the old indigo.
 *
 * Invalid hex values are dropped (the caller validates and reports them; this never emits garbage).
 */
export function brandColorPayload(colors: Partial<Record<ColorKey, string>>): BrandColorPayload {
  const out: BrandColorPayload = {};
  const unset: ColorKey[] = [];
  (Object.keys(colors) as ColorKey[]).forEach((key) => {
    const hex = normalizeHex(colors[key]);
    if (!hex) return;
    if (hex === DEFAULT_BY_KEY[key].toUpperCase()) unset.push(key);
    else out[key] = hex;
  });
  if (unset.length) out.unset = unset;
  return out;
}

/** Every colour key, for "Reset to default": clear them all. */
export const ALL_COLOR_KEYS: ColorKey[] = ['primary_color', 'accent_color', 'success_color', 'base_color'];
